import React, { useMemo, useState } from 'react';
import {
  Button,
  Collapse,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useGetProductPiecesQuery } from '../redux/api/productsApi';
import PieceCreationModal from './modals/PieceCreationModal';
import ProposalItemPieceMaterialSelector from './ProposalItemPieceMaterialSelector';

const { Text, Title } = Typography;

const getPieceLabel = (piece) =>
  piece?.internal_manufacturer_code ||
  piece?.description ||
  `Piece #${piece?.piece_id}`;

const PieceSelector = ({ product, selectedPieces = [], onPiecesChange }) => {
  const { data: existingPieces = [], isLoading: loading } =
    useGetProductPiecesQuery(product?.product_id, {
      skip: !product?.product_id,
    });

  const [showPieceModal, setShowPieceModal] = useState(false);
  const [activeKeys, setActiveKeys] = useState([]);

  const selectedIds = useMemo(
    () => selectedPieces.map((p) => String(p.piece_id)),
    [selectedPieces]
  );

  const hasCatalog = (existingPieces || []).length > 0;
  const hasSelected = selectedPieces.length > 0;

  const pieceOptions = useMemo(() => {
    const fromProduct = (existingPieces || []).map((piece) => ({
      value: String(piece.piece_id),
      piece,
      label: getPieceLabel(piece),
      searchText: `${piece.internal_manufacturer_code || ''} ${
        piece.description || ''
      }`.toLowerCase(),
    }));

    const known = new Set(fromProduct.map((o) => o.value));
    const extras = (selectedPieces || [])
      .filter((piece) => !known.has(String(piece.piece_id)))
      .map((piece) => ({
        value: String(piece.piece_id),
        piece,
        label: getPieceLabel(piece),
        searchText: `${piece.internal_manufacturer_code || ''} ${
          piece.description || ''
        }`.toLowerCase(),
      }));

    return [...fromProduct, ...extras];
  }, [existingPieces, selectedPieces]);

  const pieceById = useMemo(() => {
    const map = new Map();
    [...(existingPieces || []), ...(selectedPieces || [])].forEach((piece) => {
      map.set(String(piece.piece_id), piece);
    });
    return map;
  }, [existingPieces, selectedPieces]);

  const handlePiecesSelectChange = (values) => {
    const nextIds = (values || []).map(String);
    const prevIds = selectedIds;

    const added = nextIds.filter((id) => !prevIds.includes(id));
    const removed = prevIds.filter((id) => !nextIds.includes(id));

    let updated = selectedPieces.filter(
      (p) => !removed.includes(String(p.piece_id))
    );

    added.forEach((id) => {
      const piece = pieceById.get(id);
      if (!piece) return;
      updated = [
        ...updated,
        {
          piece_id: piece.piece_id,
          internal_manufacturer_code: piece.internal_manufacturer_code,
          description: '',
          materials: [],
          images: piece.images || [],
        },
      ];
    });

    onPiecesChange(updated);

    if (added.length > 0) {
      setActiveKeys((keys) => Array.from(new Set([...keys, ...added])));
    }
    if (removed.length > 0) {
      setActiveKeys((keys) => keys.filter((k) => !removed.includes(String(k))));
    }
  };

  const handleRemovePiece = (pieceId) => {
    const id = String(pieceId);
    onPiecesChange(selectedPieces.filter((p) => String(p.piece_id) !== id));
    setActiveKeys((keys) => keys.filter((k) => String(k) !== id));
  };

  const handleUpdatePiece = (pieceId, field, value) => {
    onPiecesChange(
      selectedPieces.map((piece) =>
        String(piece.piece_id) === String(pieceId)
          ? { ...piece, [field]: value }
          : piece
      )
    );
  };

  const handlePieceCreated = (newPiece) => {
    const pieceCopy = {
      piece_id: newPiece.piece_id,
      internal_manufacturer_code: newPiece.internal_manufacturer_code,
      description: newPiece.description || '',
      materials: [],
      images: newPiece.images || [],
    };
    onPiecesChange([...selectedPieces, pieceCopy]);
    setActiveKeys((keys) => [...keys, String(newPiece.piece_id)]);
    setShowPieceModal(false);
  };

  const createDisabled = !product?.product_id;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Title level={5} style={{ margin: 0 }}>
            Pieces
            {hasSelected ? (
              <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
                ({selectedPieces.length})
              </Text>
            ) : null}
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Optional — add parts only if this item needs them
          </Text>
        </div>
        {hasCatalog || hasSelected ? (
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setShowPieceModal(true)}
            disabled={createDisabled}
          >
            New piece
          </Button>
        ) : null}
      </div>

      {!product?.product_id ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select a product first"
        />
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : !hasCatalog && !hasSelected ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical" size={4}>
              <Text>No pieces yet for this product</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Add a piece in a few seconds — only a name is required
              </Text>
            </Space>
          }
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setShowPieceModal(true)}
          >
            Add first piece
          </Button>
        </Empty>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {hasCatalog ? (
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Or pick existing pieces…"
              style={{ width: '100%' }}
              value={selectedIds}
              options={pieceOptions}
              filterOption={(input, option) =>
                (option?.searchText || '').includes(input.toLowerCase())
              }
              optionRender={(option) => {
                const piece = option.data?.piece;
                return (
                  <div>
                    <Text strong>
                      {piece?.internal_manufacturer_code ||
                        `Piece #${piece?.piece_id}`}
                    </Text>
                    {piece?.description ? (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {piece.description}
                        </Text>
                      </div>
                    ) : null}
                  </div>
                );
              }}
              maxTagCount="responsive"
              onChange={handlePiecesSelectChange}
              notFoundContent="No matching pieces"
            />
          ) : null}

          {hasSelected ? (
            <Collapse
              activeKey={activeKeys}
              onChange={(keys) =>
                setActiveKeys(
                  Array.isArray(keys) ? keys.map(String) : [String(keys)]
                )
              }
              items={selectedPieces.map((piece) => {
                const key = String(piece.piece_id);
                const materialCount = (piece.materials || []).length;

                return {
                  key,
                  label: (
                    <Space size={8} wrap>
                      <Text strong>{getPieceLabel(piece)}</Text>
                      {materialCount > 0 ? (
                        <Tag>{materialCount} material{materialCount === 1 ? '' : 's'}</Tag>
                      ) : (
                        <Tag color="default">No materials</Tag>
                      )}
                    </Space>
                  ),
                  extra: (
                    <Popconfirm
                      title="Remove this piece?"
                      okText="Remove"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                      onConfirm={(e) => {
                        e?.stopPropagation?.();
                        handleRemovePiece(piece.piece_id);
                      }}
                      onCancel={(e) => e?.stopPropagation?.()}
                    >
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Remove ${getPieceLabel(piece)}`}
                      />
                    </Popconfirm>
                  ),
                  children: (
                    <Space
                      direction="vertical"
                      size={12}
                      style={{ width: '100%' }}
                    >
                      <Input
                        value={piece.description || ''}
                        onChange={(e) =>
                          handleUpdatePiece(
                            piece.piece_id,
                            'description',
                            e.target.value
                          )
                        }
                        placeholder="Optional description for this proposal"
                        allowClear
                      />

                      <ProposalItemPieceMaterialSelector
                        pieceId={piece.piece_id}
                        materials={piece.materials || []}
                        onMaterialsChange={(materials) => {
                          handleUpdatePiece(
                            piece.piece_id,
                            'materials',
                            materials
                          );
                        }}
                      />
                    </Space>
                  ),
                };
              })}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Select pieces above, or create a new one"
            >
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setShowPieceModal(true)}
              >
                Add piece
              </Button>
            </Empty>
          )}
        </Space>
      )}

      <PieceCreationModal
        show={showPieceModal}
        onHide={() => setShowPieceModal(false)}
        onPieceCreated={handlePieceCreated}
        productId={product?.product_id}
        simple
      />
    </div>
  );
};

export default PieceSelector;
